import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { FoodsCheckbox, FoodsTextInput, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { useTransformationReference } from "src/api/sure-api/useTransformationReference";
import { FormItemRow } from "src/components/EntityForm/FormItemRow";
import { getTransformationLineScoreKey } from "../sectionScores";
import { getTotalUsedQuantityGrams } from "./getTotalUsedQuantityGrams";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { LineScoreValue } from "../Layout/LineScoreValue";

type Step5TransformationProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    lineScores?: Record<string, string | null>;
    scoreUnit?: string;
};

type ProductTransformation = ProductFormType["productTransformations"][number];

export const Step5Transformation = (props: Step5TransformationProps) => {
    const { product, setProduct, lineScores = {}, scoreUnit = "Pts" } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { transformationReference, isLoading } = useTransformationReference();

    const references = transformationReference ?? [];

    const defaultMassGrams = useMemo(
        () => getTotalUsedQuantityGrams(product.productIngredients),
        [product.productIngredients],
    );
    const hasUninitializedMass = product.productTransformations.some(
        (transformation) => transformation.massGrams === undefined,
    );

    useEffect(() => {
        if (defaultMassGrams <= 0 || !hasUninitializedMass) {
            return;
        }

        setProduct((prev) => {
            const needsUpdate = prev.productTransformations.some(
                (transformation) => transformation.massGrams === undefined,
            );
            if (!needsUpdate) {
                return prev;
            }

            return {
                ...prev,
                productTransformations: prev.productTransformations.map((transformation) =>
                    transformation.massGrams === undefined ?
                        { ...transformation, massGrams: defaultMassGrams }
                    :   transformation,
                ),
            };
        });
    }, [defaultMassGrams, hasUninitializedMass, setProduct]);

    if (isLoading) {
        return (
            <Box sx={{ padding: theme.spacing(3), display: "flex", justifyContent: "center" }}>
                <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
            </Box>
        );
    }

    if (transformationReference === undefined) {
        return (
            <Box sx={{ padding: theme.spacing(3) }}>
                <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                    {t("common.unexpected_error", "Unexpected error")}
                </Typography>
            </Box>
        );
    }

    const selectedRefIds = new Set(product.productTransformations.map((pt) => pt.transformationReferenceId));

    const updateTransformation = (refId: number, patch: Partial<ProductTransformation>) => {
        setProduct((prev) => ({
            ...prev,
            productTransformations: prev.productTransformations.map((pt) =>
                pt.transformationReferenceId === refId ? { ...pt, ...patch } : pt,
            ),
        }));
    };

    const toggleTransformation = (refId: number, checked: boolean) => {
        setProduct((prev) => {
            const alreadySelected = prev.productTransformations.some((pt) => pt.transformationReferenceId === refId);
            if (checked) {
                if (alreadySelected) return prev;
                return {
                    ...prev,
                    productTransformations: [
                        ...prev.productTransformations,
                        {
                            transformationReferenceId: refId,
                            massGrams: defaultMassGrams > 0 ? defaultMassGrams : undefined,
                            organization: null,
                            position: prev.productTransformations.length,
                        },
                    ],
                };
            }
            return {
                ...prev,
                productTransformations: prev.productTransformations.filter(
                    (pt) => pt.transformationReferenceId !== refId,
                ),
            };
        });
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(2),
                padding: theme.spacing(3),
            }}
        >
            <Stack data-testid="processing-list" sx={{ gap: theme.spacing(2) }}>
                {references.map((ref) => {
                    const transformation = product.productTransformations.find(
                        (pt) => pt.transformationReferenceId === ref.id,
                    );
                    return (
                        <TransformationReferenceRow
                            key={ref.id}
                            refId={ref.id}
                            refLabel={ref.label}
                            checked={selectedRefIds.has(ref.id)}
                            transformation={transformation}
                            lineScore={
                                transformation === undefined ? null : (
                                    (lineScores[getTransformationLineScoreKey(transformation) ?? ""] ?? null)
                                )
                            }
                            onToggle={toggleTransformation}
                            onUpdate={updateTransformation}
                            scoreUnit={scoreUnit}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
};

type TransformationReferenceRowProps = {
    refId: number;
    refLabel: string;
    checked: boolean;
    transformation: ProductTransformation | undefined;
    lineScore: string | null;
    scoreUnit: string;
    onToggle: (refId: number, checked: boolean) => void;
    onUpdate: (refId: number, patch: Partial<ProductTransformation>) => void;
};

const TransformationReferenceRow = (props: TransformationReferenceRowProps) => {
    const { refId, refLabel, checked, transformation, lineScore, scoreUnit, onToggle, onUpdate } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const massLabel = t("product.v2.transformation.processing_input_mass", "Processing input mass");

    const displayedScore = checked ? lineScore : null;

    const checkbox = (
        <FoodsCheckbox
            values={[{ id: refId, name: refLabel }]}
            selectedItems={checked ? [{ id: refId, name: refLabel }] : []}
            onChange={(selectedItems) => onToggle(refId, selectedItems.length > 0)}
            FormControlLabelProps={{
                sx: {
                    gap: theme.spacing(1.5),
                    margin: 0,
                    minWidth: 0,
                    ".MuiTypography-root": {
                        ...theme.typography["body-regular"],
                        color: theme.custom.grey[2500],
                    },
                },
            }}
            CheckboxProps={{ sx: { padding: 0 } }}
        />
    );

    return (
        <FormItemRow testId={`processing-row-${refId}`}>
            <Stack
                direction="row"
                alignItems="center"
                sx={{ flex: { xs: "1 1 100%", sm: 1 }, flexWrap: "wrap", gap: theme.spacing(3), minWidth: 0 }}
            >
                <Box sx={{ flexShrink: 0, width: theme.custom.formField.compact.optionWidth }}>{checkbox}</Box>
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ gap: theme.spacing(1), flexWrap: "wrap", minWidth: 0 }}
                >
                    <Typography variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                        {massLabel}
                    </Typography>
                    <FoodsTextInput
                        value={String(transformation?.massGrams ?? "")}
                        unit={t("weight.g", "g")}
                        disabled={!checked}
                        onChange={(value) =>
                            onUpdate(refId, {
                                massGrams: value === "" ? null : Number.parseInt(value, 10),
                            })
                        }
                        ExtraInputProps={{
                            inputProps: { "aria-label": massLabel, inputMode: "numeric", min: 0 },
                            sx: {
                                "& input[type='number']": { appearance: "textfield", MozAppearance: "textfield" },
                                "& input[type='number']::-webkit-inner-spin-button, & input[type='number']::-webkit-outer-spin-button":
                                    {
                                        margin: 0,
                                        WebkitAppearance: "none",
                                    },
                            },
                            type: "number",
                        }}
                        StackProps={{
                            sx: { flexShrink: 0, width: theme.custom.formField.compact.width },
                        }}
                    />
                </Stack>
            </Stack>
            <Box
                sx={{
                    display: "flex",
                    flexShrink: 0,
                    justifyContent: "flex-end",
                    width: { xs: "100%", sm: theme.custom.scoreValue.inline.width },
                }}
            >
                <LineScoreValue value={displayedScore} unit={scoreUnit} />
            </Box>
        </FormItemRow>
    );
};
